import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, Building2, Download, FileText, HelpCircle, Check, AlertTriangle, Trash2, ChevronDown, ChevronUp, ExternalLink, Calendar, CreditCard, Plus, X, PoundSterling } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { API_URL } from '../../config/api';

// Your tracked companies
const TRACKED_COMPANIES = [
  { number: '15429390', name: 'JUST RENOVATED LTD' },
  { number: '13801558', name: 'JUST CLEANED LIMITED' }
];

const AccountsFiling = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [showBankGuide, setShowBankGuide] = useState(true);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [validation, setValidation] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  
  // Bookkeeping data
  const [bookkeepingData, setBookkeepingData] = useState(null);
  const [loadingBookkeeping, setLoadingBookkeeping] = useState(false);
  
  // Bank statement entries
  const [bankEntries, setBankEntries] = useState([]);
  const [moneyOwed, setMoneyOwed] = useState([]); // Debtors - people who owe you
  const [moneyYouOwe, setMoneyYouOwe] = useState([]); // Creditors - you owe them
  const [equipment, setEquipment] = useState([]); // Fixed assets
  
  // Form state
  const [formData, setFormData] = useState({
    period_start: '',
    period_end: '',
    cash_at_bank: '',
    debtors: '',
    other_current_assets: '',
    fixed_assets: '',
    creditors_within_one_year: '',
    creditors_after_one_year: '',
    called_up_share_capital: '1',
    profit_and_loss_account: '',
    average_employees: '1',
    director_name: '',
    approved_date: '',
    notes: ''
  });

  useEffect(() => {
    loadSavedAccounts();
  }, []);
  
  // Load bookkeeping data when company and period are selected
  useEffect(() => {
    if (selectedCompany && formData.period_start && formData.period_end) {
      loadBookkeepingData();
    }
  }, [selectedCompany, formData.period_start, formData.period_end]);
  
  const loadBookkeepingData = async () => {
    if (!selectedCompany || !formData.period_start || !formData.period_end) return;
    
    setLoadingBookkeeping(true);
    try {
      const params = new URLSearchParams({
        company_number: selectedCompany.number,
        start_date: formData.period_start,
        end_date: formData.period_end
      });
      
      const res = await fetch(`${API_URL}/api/admin/bookkeeping/reports/profit-loss?${params}`);
      if (res.ok) {
        const data = await res.json();
        setBookkeepingData(data);
      }
    } catch (err) {
      console.error('Failed to load bookkeeping data:', err);
    } finally {
      setLoadingBookkeeping(false);
    }
  };
  
  const applyBookkeepingData = () => {
    if (!bookkeepingData) return;
    
    // The P&L account in the balance sheet is the accumulated profit/loss
    setFormData(prev => ({
      ...prev,
      profit_and_loss_account: bookkeepingData.net_profit.toFixed(2)
    }));
    
    setSuccess('Profit & Loss figure imported from your bookkeeping records!');
  };

  // Calculate months needed for bank statements
  const requiredMonths = useMemo(() => {
    if (!formData.period_start || !formData.period_end) return [];
    
    const months = [];
    const start = new Date(formData.period_start);
    const end = new Date(formData.period_end);
    
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    while (current <= end) {
      months.push({
        key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
        label: current.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        month: current.getMonth(),
        year: current.getFullYear()
      });
      current.setMonth(current.getMonth() + 1);
    }
    return months;
  }, [formData.period_start, formData.period_end]);

  // Auto-calculate from entries
  useEffect(() => {
    // Calculate closing bank balance
    const lastEntry = bankEntries.find(e => e.month === requiredMonths[requiredMonths.length - 1]?.key);
    if (lastEntry?.closing) {
      setFormData(prev => ({ ...prev, cash_at_bank: lastEntry.closing }));
    }
    
    // Calculate debtors
    const totalDebtors = moneyOwed.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    if (totalDebtors > 0) {
      setFormData(prev => ({ ...prev, debtors: totalDebtors.toString() }));
    }
    
    // Calculate creditors
    const totalCreditors = moneyYouOwe.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    if (totalCreditors > 0) {
      setFormData(prev => ({ ...prev, creditors_within_one_year: totalCreditors.toString() }));
    }
    
    // Calculate fixed assets
    const totalEquipment = equipment.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
    if (totalEquipment > 0) {
      setFormData(prev => ({ ...prev, fixed_assets: totalEquipment.toString() }));
    }
  }, [bankEntries, moneyOwed, moneyYouOwe, equipment, requiredMonths]);

  const loadSavedAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/accounts-filing/saved`);
      const data = await res.json();
      setSavedAccounts(data.accounts || []);
    } catch (err) {
      console.error('Failed to load saved accounts:', err);
    }
  };

  const selectCompany = async (company) => {
    setSelectedCompany(company);
    setLoading(true);
    setError('');
    setCompanyInfo(null);
    setActiveStep(1);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/accounts-filing/company/${company.number}/info`);
      if (!res.ok) throw new Error('Failed to fetch company info');
      const data = await res.json();
      setCompanyInfo(data);
      
      // Pre-fill period dates
      if (data.accounts?.next_made_up_to) {
        // This is the period end date
        const periodEnd = new Date(data.accounts.next_made_up_to);
        let periodStart;
        
        if (data.accounts?.last_accounts_made_up_to) {
          // Start from day after last accounts
          periodStart = new Date(data.accounts.last_accounts_made_up_to);
          periodStart.setDate(periodStart.getDate() + 1);
        } else {
          // First year accounts - use incorporation date or 1 year before period end
          if (data.company?.date_of_creation) {
            periodStart = new Date(data.company.date_of_creation);
          } else {
            periodStart = new Date(periodEnd);
            periodStart.setFullYear(periodStart.getFullYear() - 1);
            periodStart.setDate(periodStart.getDate() + 1);
          }
        }
        
        setFormData(prev => ({
          ...prev,
          period_start: periodStart.toISOString().split('T')[0],
          period_end: periodEnd.toISOString().split('T')[0]
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidation(null);
  };

  // Bank entry handlers
  const updateBankEntry = (monthKey, field, value) => {
    setBankEntries(prev => {
      const existing = prev.find(e => e.month === monthKey);
      if (existing) {
        return prev.map(e => e.month === monthKey ? { ...e, [field]: value } : e);
      } else {
        return [...prev, { month: monthKey, [field]: value }];
      }
    });
  };

  const getBankEntry = (monthKey) => {
    return bankEntries.find(e => e.month === monthKey) || {};
  };

  // Add item handlers
  const addMoneyOwed = () => {
    setMoneyOwed(prev => [...prev, { id: Date.now(), description: '', amount: '' }]);
  };

  const updateMoneyOwed = (id, field, value) => {
    setMoneyOwed(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeMoneyOwed = (id) => {
    setMoneyOwed(prev => prev.filter(item => item.id !== id));
  };

  const addMoneyYouOwe = () => {
    setMoneyYouOwe(prev => [...prev, { id: Date.now(), description: '', amount: '' }]);
  };

  const updateMoneyYouOwe = (id, field, value) => {
    setMoneyYouOwe(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeMoneyYouOwe = (id) => {
    setMoneyYouOwe(prev => prev.filter(item => item.id !== id));
  };

  const addEquipment = () => {
    setEquipment(prev => [...prev, { id: Date.now(), description: '', value: '' }]);
  };

  const updateEquipment = (id, field, value) => {
    setEquipment(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeEquipment = (id) => {
    setEquipment(prev => prev.filter(item => item.id !== id));
  };

  const calculateTotals = () => {
    const cash = parseFloat(formData.cash_at_bank) || 0;
    const debtors = parseFloat(formData.debtors) || 0;
    const otherCurrent = parseFloat(formData.other_current_assets) || 0;
    const fixed = parseFloat(formData.fixed_assets) || 0;
    
    const currentAssets = cash + debtors + otherCurrent;
    const totalAssets = currentAssets + fixed;
    
    const creditorsShort = parseFloat(formData.creditors_within_one_year) || 0;
    const creditorsLong = parseFloat(formData.creditors_after_one_year) || 0;
    const totalLiabilities = creditorsShort + creditorsLong;
    
    const netAssets = totalAssets - totalLiabilities;
    
    const shareCapital = parseFloat(formData.called_up_share_capital) || 0;
    const retained = parseFloat(formData.profit_and_loss_account) || 0;
    const totalEquity = shareCapital + retained;
    
    const isBalanced = Math.abs(netAssets - totalEquity) < 0.01;
    
    return {
      currentAssets,
      totalAssets,
      totalLiabilities,
      netAssets,
      totalEquity,
      isBalanced,
      difference: netAssets - totalEquity
    };
  };

  // Auto-calculate P&L to balance
  const autoBalancePL = () => {
    const cash = parseFloat(formData.cash_at_bank) || 0;
    const debtors = parseFloat(formData.debtors) || 0;
    const otherCurrent = parseFloat(formData.other_current_assets) || 0;
    const fixed = parseFloat(formData.fixed_assets) || 0;
    const creditorsShort = parseFloat(formData.creditors_within_one_year) || 0;
    const creditorsLong = parseFloat(formData.creditors_after_one_year) || 0;
    const shareCapital = parseFloat(formData.called_up_share_capital) || 1;
    
    const netAssets = (cash + debtors + otherCurrent + fixed) - (creditorsShort + creditorsLong);
    const requiredPL = netAssets - shareCapital;
    
    setFormData(prev => ({ ...prev, profit_and_loss_account: requiredPL.toFixed(2) }));
  };

  const saveAccounts = async () => {
    if (!selectedCompany) return;
    
    const totals = calculateTotals();
    if (!totals.isBalanced) {
      setError(`Balance sheet doesn't balance. Net Assets (£${totals.netAssets.toFixed(2)}) must equal Total Equity (£${totals.totalEquity.toFixed(2)})`);
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const payload = {
        company_number: selectedCompany.number,
        company_name: selectedCompany.name,
        period_start: formData.period_start,
        period_end: formData.period_end,
        cash_at_bank: parseFloat(formData.cash_at_bank) || 0,
        debtors: parseFloat(formData.debtors) || 0,
        other_current_assets: parseFloat(formData.other_current_assets) || 0,
        fixed_assets: parseFloat(formData.fixed_assets) || 0,
        creditors_within_one_year: parseFloat(formData.creditors_within_one_year) || 0,
        creditors_after_one_year: parseFloat(formData.creditors_after_one_year) || 0,
        called_up_share_capital: parseFloat(formData.called_up_share_capital) || 1,
        profit_and_loss_account: parseFloat(formData.profit_and_loss_account) || 0,
        average_employees: parseInt(formData.average_employees) || 0,
        director_name: formData.director_name,
        approved_date: formData.approved_date,
        notes: formData.notes
      };
      
      const res = await fetch(`${API_URL}/api/admin/accounts-filing/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save accounts');
      }
      
      setValidation(data.validation);
      setSuccess('Accounts saved successfully! You can now download the PDF.');
      loadSavedAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (accountId) => {
    window.open(`${API_URL}/api/admin/accounts-filing/saved/${accountId}/pdf`, '_blank');
  };

  const deleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this saved account?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/accounts-filing/saved/${accountId}`, {
        method: 'DELETE'
      });
      loadSavedAccounts();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const loadSavedAccount = async (account) => {
    const company = TRACKED_COMPANIES.find(c => c.number === account.company_number);
    if (company) {
      setSelectedCompany(company);
      try {
        const res = await fetch(`${API_URL}/api/admin/accounts-filing/company/${company.number}/info`);
        if (res.ok) {
          const data = await res.json();
          setCompanyInfo(data);
        }
      } catch (err) {
        console.error('Error loading company info:', err);
      }
    }
    
    const data = account.accounts_data;
    setFormData({
      period_start: data.period_start || '',
      period_end: data.period_end || '',
      cash_at_bank: data.cash_at_bank?.toString() || '',
      debtors: data.debtors?.toString() || '',
      other_current_assets: data.other_current_assets?.toString() || '',
      fixed_assets: data.fixed_assets?.toString() || '',
      creditors_within_one_year: data.creditors_within_one_year?.toString() || '',
      creditors_after_one_year: data.creditors_after_one_year?.toString() || '',
      called_up_share_capital: data.called_up_share_capital?.toString() || '1',
      profit_and_loss_account: data.profit_and_loss_account?.toString() || '',
      average_employees: data.average_employees?.toString() || '1',
      director_name: data.director_name || '',
      approved_date: data.approved_date || '',
      notes: data.notes || ''
    });
    setActiveStep(3);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const totals = calculateTotals();

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="accounts-filing-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Calculator className="w-7 h-7 text-green-600" />
          Accounts Filing Preparation
        </h1>
        <p className="text-gray-500 mt-1">Prepare your micro-entity accounts step by step</p>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-gray-600" />
          Select Company
        </h2>
        
        <div className="grid gap-3 md:grid-cols-2">
          {TRACKED_COMPANIES.map((company) => (
            <button
              key={company.number}
              onClick={() => selectCompany(company)}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                selectedCompany?.number === company.number
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium text-gray-900">{company.name}</div>
              <div className="text-sm text-gray-500">{company.number}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading company information...</p>
        </div>
      )}

      {companyInfo && !loading && (
        <>
          {/* Accounts Status Alert */}
          {companyInfo.accounts?.overdue && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Accounts Overdue!</h3>
                <p className="text-red-700 text-sm">Your accounts were due on {formatDate(companyInfo.accounts?.next_due)}. File immediately to minimize penalties.</p>
              </div>
            </div>
          )}

          {/* Period Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounting Period</h2>
            
            <div className="grid gap-4 md:grid-cols-4 mb-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Period Start</div>
                <div className="text-lg font-semibold text-gray-900">{formatDate(formData.period_start)}</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Period End</div>
                <div className="text-lg font-semibold text-gray-900">{formatDate(formData.period_end)}</div>
              </div>
              <div className={`p-4 rounded-lg ${companyInfo.accounts?.overdue ? 'bg-red-50' : 'bg-gray-50'}`}>
                <div className="text-sm text-gray-500">Filing Deadline</div>
                <div className={`text-lg font-semibold ${companyInfo.accounts?.overdue ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatDate(companyInfo.accounts?.next_due)}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Duration</div>
                <div className="text-lg font-semibold text-gray-900">{requiredMonths.length} months</div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period Start Date</label>
                <Input
                  type="date"
                  value={formData.period_start}
                  onChange={(e) => handleInputChange('period_start', e.target.value)}
                  className="h-11"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period End Date</label>
                <Input
                  type="date"
                  value={formData.period_end}
                  onChange={(e) => handleInputChange('period_end', e.target.value)}
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Step Progress */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between">
              {[
                { num: 1, label: 'Bank Statements' },
                { num: 2, label: 'Assets & Debts' },
                { num: 3, label: 'Review & File' }
              ].map((step, i) => (
                <button
                  key={step.num}
                  onClick={() => setActiveStep(step.num)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg transition-colors ${
                    activeStep === step.num
                      ? 'bg-green-100 text-green-700'
                      : activeStep > step.num
                        ? 'bg-green-50 text-green-600'
                        : 'text-gray-400'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    activeStep === step.num
                      ? 'bg-green-600 text-white'
                      : activeStep > step.num
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {activeStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                  </span>
                  <span className="hidden md:inline font-medium">{step.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 1: Bank Statements */}
          {activeStep === 1 && requiredMonths.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  Bank Statements Needed
                </h2>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">📋 What You Need</h3>
                <p className="text-blue-800 text-sm mb-2">
                  Get your bank statements for each month below. You only need the <strong>closing balance</strong> from the <strong>last month</strong> ({requiredMonths[requiredMonths.length - 1]?.label}).
                </p>
                <p className="text-blue-700 text-sm">
                  The closing balance on your {requiredMonths[requiredMonths.length - 1]?.label} statement = your "Cash at Bank" figure.
                </p>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 px-4">
                  <div className="col-span-4">Month</div>
                  <div className="col-span-4">Closing Balance (£)</div>
                  <div className="col-span-4">Status</div>
                </div>
                
                {requiredMonths.map((month, idx) => {
                  const entry = getBankEntry(month.key);
                  const isLast = idx === requiredMonths.length - 1;
                  
                  return (
                    <div 
                      key={month.key} 
                      className={`grid grid-cols-12 gap-4 items-center p-4 rounded-lg ${
                        isLast ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="col-span-4">
                        <span className={`font-medium ${isLast ? 'text-green-700' : 'text-gray-900'}`}>
                          {month.label}
                        </span>
                        {isLast && (
                          <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">
                            IMPORTANT
                          </span>
                        )}
                      </div>
                      <div className="col-span-4">
                        {isLast ? (
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="e.g., 5000.00"
                            value={entry.closing || ''}
                            onChange={(e) => updateBankEntry(month.key, 'closing', e.target.value)}
                            className="h-10"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">Not needed</span>
                        )}
                      </div>
                      <div className="col-span-4">
                        {isLast ? (
                          entry.closing ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <Check className="w-4 h-4" /> Entered
                            </span>
                          ) : (
                            <span className="text-orange-500 text-sm">Enter closing balance →</span>
                          )
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-yellow-800 mb-2">💡 Quick Tip</h3>
                <p className="text-yellow-700 text-sm">
                  Log into your online banking, go to your business account, and find the statement for <strong>{requiredMonths[requiredMonths.length - 1]?.label}</strong>. 
                  The closing balance (the amount at the end of the month) is what you enter above.
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => setActiveStep(2)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Next: Assets & Debts →
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Assets & Debts */}
          {activeStep === 2 && (
            <div className="space-y-6 mb-6">
              {/* Money Owed TO You (Debtors) */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Money Owed TO You</h2>
                    <p className="text-sm text-gray-500">Customers who haven't paid yet (as of {formatDate(formData.period_end)})</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addMoneyOwed} className="flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>

                {moneyOwed.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <p>No outstanding invoices? Click "Add" if customers owe you money.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {moneyOwed.map(item => (
                      <div key={item.id} className="flex gap-3 items-center">
                        <Input
                          placeholder="Who owes you? (e.g., ABC Ltd)"
                          value={item.description}
                          onChange={(e) => updateMoneyOwed(item.id, 'description', e.target.value)}
                          className="flex-1 h-10"
                        />
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Amount"
                            value={item.amount}
                            onChange={(e) => updateMoneyOwed(item.id, 'amount', e.target.value)}
                            className="h-10 pl-7"
                          />
                        </div>
                        <button onClick={() => removeMoneyOwed(item.id)} className="p-2 text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="text-right font-medium text-green-600">
                      Total: £{moneyOwed.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              {/* Money YOU Owe (Creditors) */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Money YOU Owe</h2>
                    <p className="text-sm text-gray-500">Unpaid bills, supplier invoices, credit cards (as of {formatDate(formData.period_end)})</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addMoneyYouOwe} className="flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>

                {moneyYouOwe.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <p>No unpaid bills? Click "Add" if you owe money to suppliers, HMRC, etc.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {moneyYouOwe.map(item => (
                      <div key={item.id} className="flex gap-3 items-center">
                        <Input
                          placeholder="Who do you owe? (e.g., Supplier Ltd, HMRC)"
                          value={item.description}
                          onChange={(e) => updateMoneyYouOwe(item.id, 'description', e.target.value)}
                          className="flex-1 h-10"
                        />
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Amount"
                            value={item.amount}
                            onChange={(e) => updateMoneyYouOwe(item.id, 'amount', e.target.value)}
                            className="h-10 pl-7"
                          />
                        </div>
                        <button onClick={() => removeMoneyYouOwe(item.id)} className="p-2 text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="text-right font-medium text-red-600">
                      Total: £{moneyYouOwe.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              {/* Equipment / Fixed Assets */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Equipment & Assets</h2>
                    <p className="text-sm text-gray-500">Vehicles, computers, machinery, tools owned by the company</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addEquipment} className="flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>

                {equipment.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <p>No equipment? Most service companies have none - that's fine!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {equipment.map(item => (
                      <div key={item.id} className="flex gap-3 items-center">
                        <Input
                          placeholder="What is it? (e.g., Van, Computer)"
                          value={item.description}
                          onChange={(e) => updateEquipment(item.id, 'description', e.target.value)}
                          className="flex-1 h-10"
                        />
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Value"
                            value={item.value}
                            onChange={(e) => updateEquipment(item.id, 'value', e.target.value)}
                            className="h-10 pl-7"
                          />
                        </div>
                        <button onClick={() => removeEquipment(item.id)} className="p-2 text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="text-right font-medium text-blue-600">
                      Total: £{equipment.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveStep(1)}>
                  ← Back
                </Button>
                <Button 
                  onClick={() => setActiveStep(3)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Next: Review & File →
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review & File */}
          {activeStep === 3 && (
            <div className="space-y-6 mb-6">
              {/* Bookkeeping Import */}
              {bookkeepingData && bookkeepingData.transaction_count > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                        📊 Import from Bookkeeping
                      </h3>
                      <p className="text-purple-700 text-sm mt-1">
                        You have {bookkeepingData.transaction_count} transactions recorded for this period.
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="text-green-600 font-medium">Income: £{bookkeepingData.income.total.toFixed(2)}</span>
                        <span className="mx-2">|</span>
                        <span className="text-red-600 font-medium">Expenses: £{bookkeepingData.expenses.total.toFixed(2)}</span>
                        <span className="mx-2">|</span>
                        <span className={`font-medium ${bookkeepingData.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          Net: £{bookkeepingData.net_profit.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={applyBookkeepingData}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Use This P&L Figure
                    </Button>
                  </div>
                </div>
              )}
              
              {bookkeepingData && bookkeepingData.transaction_count === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-yellow-800 text-sm">
                    💡 <strong>Tip:</strong> You haven't entered any transactions in Bookkeeping for this period. 
                    <a href="/admin/bookkeeping" className="underline ml-1">Go to Bookkeeping</a> to enter your income and expenses, 
                    then come back here to auto-populate your P&L figure.
                  </p>
                </div>
              )}
            
              {/* Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Review Your Figures</h2>

                <div className="grid gap-4 md:grid-cols-2 mb-6">
                  {/* Assets */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-3">Assets (What You Own)</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Cash at Bank:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.cash_at_bank}
                          onChange={(e) => handleInputChange('cash_at_bank', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>Money Owed to You:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.debtors}
                          onChange={(e) => handleInputChange('debtors', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>Equipment:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.fixed_assets}
                          onChange={(e) => handleInputChange('fixed_assets', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between font-semibold pt-2 border-t border-green-200">
                        <span>Total Assets:</span>
                        <span>£{totals.totalAssets.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities */}
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h3 className="font-semibold text-red-800 mb-3">Liabilities (What You Owe)</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Money You Owe:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.creditors_within_one_year}
                          onChange={(e) => handleInputChange('creditors_within_one_year', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>Long-term Debt:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.creditors_after_one_year}
                          onChange={(e) => handleInputChange('creditors_after_one_year', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between font-semibold pt-2 border-t border-red-200">
                        <span>Total Liabilities:</span>
                        <span>£{totals.totalLiabilities.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net Assets & Equity */}
                <div className="grid gap-4 md:grid-cols-2 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2">Net Assets</h3>
                    <p className="text-2xl font-bold text-blue-900">£{totals.netAssets.toFixed(2)}</p>
                    <p className="text-xs text-blue-600 mt-1">Assets minus Liabilities</p>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold text-purple-800 mb-2">Shareholders' Equity</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span>Share Capital:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.called_up_share_capital}
                          onChange={(e) => handleInputChange('called_up_share_capital', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Profit/Loss:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.profit_and_loss_account}
                          onChange={(e) => handleInputChange('profit_and_loss_account', e.target.value)}
                          className="w-28 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between font-semibold pt-2 border-t border-purple-200">
                        <span>Total Equity:</span>
                        <span>£{totals.totalEquity.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Check */}
                <div className={`p-4 rounded-lg ${totals.isBalanced ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {totals.isBalanced ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      )}
                      <span className={`font-semibold ${totals.isBalanced ? 'text-green-800' : 'text-red-800'}`}>
                        {totals.isBalanced ? 'Balance Sheet Balanced ✓' : 'Balance Sheet Not Balanced'}
                      </span>
                    </div>
                    {!totals.isBalanced && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={autoBalancePL}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Auto-fix P&L
                      </Button>
                    )}
                  </div>
                  {!totals.isBalanced && (
                    <p className="text-red-700 text-sm mt-2">
                      Difference: £{Math.abs(totals.difference).toFixed(2)} — Net Assets must equal Total Equity. 
                      Click "Auto-fix P&L" to set the Profit/Loss figure automatically.
                    </p>
                  )}
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
                
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Average Employees</label>
                    <Input
                      type="number"
                      value={formData.average_employees}
                      onChange={(e) => handleInputChange('average_employees', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Director Name</label>
                    <Input
                      type="text"
                      placeholder="Your full name"
                      value={formData.director_name}
                      onChange={(e) => handleInputChange('director_name', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Approval Date</label>
                    <Input
                      type="date"
                      value={formData.approved_date}
                      onChange={(e) => handleInputChange('approved_date', e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  {success}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setActiveStep(2)} className="flex-1">
                  ← Back
                </Button>
                <Button
                  onClick={saveAccounts}
                  disabled={saving || !totals.isBalanced || !formData.period_start || !formData.period_end}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {saving ? 'Saving...' : 'Save & Generate PDF'}
                </Button>
              </div>

              {/* Filing Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Next Steps: File with Companies House
                </h3>
                <ol className="space-y-2 text-blue-800 text-sm">
                  <li className="flex gap-2">
                    <span className="font-bold">1.</span>
                    <span>Save your accounts above and download the PDF summary</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">2.</span>
                    <span>Go to Companies House WebFiling:</span>
                  </li>
                </ol>
                <a 
                  href="https://www.gov.uk/file-your-company-accounts-and-tax-return" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Open Companies House Filing <ExternalLink className="w-4 h-4" />
                </a>
                <ol start={3} className="mt-4 space-y-2 text-blue-800 text-sm">
                  <li className="flex gap-2">
                    <span className="font-bold">3.</span>
                    <span>Select "Micro-entity accounts" when asked for account type</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">4.</span>
                    <span>Enter the figures from your PDF into the form</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">5.</span>
                    <span>Review and submit (it's FREE for micro-entity accounts!)</span>
                  </li>
                </ol>
              </div>
            </div>
          )}
        </>
      )}

      {/* Saved Accounts */}
      {savedAccounts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-600" />
            Saved Accounts
          </h2>
          
          <div className="space-y-3">
            {savedAccounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <button
                    onClick={() => loadSavedAccount(account)}
                    className="text-left hover:text-green-600"
                  >
                    <div className="font-medium text-gray-900">{account.company_name}</div>
                    <div className="text-sm text-gray-500">
                      Period ending: {formatDate(account.period_end)}
                    </div>
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    account.status === 'filed' ? 'bg-green-100 text-green-700' :
                    account.status === 'ready' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {account.status}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdf(account.id)}
                    className="flex items-center gap-1"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </Button>
                  <button
                    onClick={() => deleteAccount(account.id)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsFiling;
